import { AxiosError } from "axios";
import type { AxiosResponse } from "axios";

import { CancelError } from "@/error/CancelError";
import { errorMessage } from "@/error/errorUnwrapper";
import { useStatisticsStore } from "@/store/statistics";
import { piserverAPIUrl } from "@/utils/env/Environment";
import type { UnknownRecord } from "@/utils/type";

import { BackendError } from "../BackendError";
import { createApiClient } from "../http/api.config";
import { BaseRpcBackend } from "./BaseRpcBackend";
import type { RpcBackend } from "./RpcBackend";
import { translateRPCPath } from "./natsTopicMapper";
import { createCallContext, createCallHeaders } from "./requestHelper";
import type { CallContext } from "./requestHelper";
import { handleResponse } from "./responseHelper";

export const SERVER_PATH = `piplanning/6/room/server/`;

export function rpcUrl(path: string, args: unknown[]) {
  return `ch/rentouch/${path}/${args.join("/")}`;
}

export class HttpRpcBackend extends BaseRpcBackend<AxiosResponse> implements RpcBackend {
  private sessionId!: string;
  private apiClient = createApiClient(piserverAPIUrl);

  constructor() {
    super("http");
  }

  init(sessionId: string) {
    this.sessionId = sessionId;
  }

  async doCall<T>(
    base: string,
    path: string,
    args: unknown[] = [],
    kwargs?: UnknownRecord,
    timeout?: number,
  ): Promise<T> {
    const context = createCallContext(base, path, args, kwargs, timeout, 0);
    try {
      const res = await this.callBackend(context);
      useStatisticsStore().addOk("calls");
      return handleResponse(res.data, context) as T;
      // FIXME: Simply casting the response is not a good idea because we are
      // basically removing all advantages of Typescript on the calling
      // functions.
      //
      // To conform to the signature of BackendSession we are still casting here,
      // but this should be changed to a parsing strategy where the format is
      // validated before being converted.
    } catch (err) {
      useStatisticsStore().addFail("calls", err);
      if (err instanceof CancelError) {
        throw err;
      }
      if (err instanceof AxiosError) {
        if (err.code === AxiosError.ERR_BAD_RESPONSE && err.response?.data) {
          return handleResponse(err.response.data, context) as T;
        }
      }
      const message = errorMessage(err, "Unknown");
      throw new BackendError(message, `HTTP Error while calling RPC method ${base + path}`, {
        request: context,
        response: { message },
      });
    }
  }

  async doCallBackend(context: CallContext) {
    return await this.apiClient.get(rpcUrl(context.base + context.path, context.args), {
      params: context.kwargs,
      headers: createCallHeaders(context),
      timeout: context.requestTimeout,
    });
  }

  @translateRPCPath
  async sessionCall<T>(path: string, args: unknown[] = [], kwargs?: UnknownRecord) {
    const base = "piplanning/6/room/session/";
    return await this.doCall<T>(base, path, [this.sessionId, ...args], kwargs);
  }

  @translateRPCPath
  async staticSessionCall<T>(path: string, args: unknown[] = [], kwargs?: UnknownRecord) {
    return await this.doCall<T>("piplanning/6/room/session/", path, args, kwargs);
  }

  @translateRPCPath
  async boardCall<T>(path: string, args: unknown[], kwargs?: UnknownRecord) {
    return await this.doCall<T>("piplanning/6/room/board/", path, args, kwargs);
  }

  @translateRPCPath
  async stickyCall<T>(path: string, args: unknown[], kwargs?: UnknownRecord) {
    return await this.doCall<T>("piplanning/6/room/sticky/", path, args, kwargs);
  }

  @translateRPCPath
  async flexBoardCall<T>(path: string, args: unknown[] = [], kwargs?: UnknownRecord) {
    return await this.doCall<T>("piplanning/6/room/session/", path, args, kwargs);
  }

  @translateRPCPath
  async roomCall<T>(path: string, args: unknown[] = []) {
    return await this.doCall<T>("piplanning/6/room/", path, args);
  }

  @translateRPCPath
  async adminCall<T>(path: string, args: unknown[] = []) {
    return await this.doCall<T>("piplanning/6/room/", path, args);
  }

  @translateRPCPath
  async almCall<T>(path: string, args: unknown[] = []) {
    return await this.doCall<T>("piplanning/6/room/", path, args);
  }

  @translateRPCPath
  almToolCall<T>(_path: string, _args: unknown[], _timeout?: number): Promise<T> {
    throw Error("unsupported http call to almsync");
  }

  @translateRPCPath
  async companyCall<T>(path: string, args: unknown[] = []) {
    return await this.doCall<T>("piplanning/3/room/company/", path, args);
  }

  @translateRPCPath
  async serverCall<T>(
    path: string,
    args: unknown[] = [],
    kwargs?: UnknownRecord,
    timeout?: number,
  ) {
    return await this.doCall<T>(SERVER_PATH, path, args, kwargs, timeout);
  }

  @translateRPCPath
  async statusCall<T>(path: string, args: unknown[] = [], kwargs?: UnknownRecord) {
    return await this.doCall<T>("piplanning/6/room/status/", path, args, kwargs);
  }

  userCall<T>(_path: string): Promise<T> {
    throw Error("unsupported http call to almsync");
  }
}
